// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { Button, Card, Skeleton, useToast } from "@chotca/ui";
import { useMe } from "@chotca/api-client";
import {
  useDeletePostingTemplate,
  usePostingTemplates,
  useUpdatePostingTemplate,
} from "@chotca/api-client";

export function TemplatesClient() {
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId ?? "";
  const templates = usePostingTemplates(orgId || null);
  const update = useUpdatePostingTemplate(orgId);
  const remove = useDeletePostingTemplate(orgId);
  const { toast } = useToast();

  if (!orgId) {
    return (
      <p className="text-sm text-ash">
        Bạn chưa có doanh nghiệp — hãy hoàn tất onboarding trước.
      </p>
    );
  }

  if (templates.isLoading) {
    return <Skeleton className="h-64 rounded-2xl" />;
  }

  const orgTemplates = templates.data?.items.filter((t) => !t.isSystem) ?? [];
  const systemTemplates = templates.data?.items.filter((t) => t.isSystem) ?? [];

  async function rename(templateId: string, currentName: string) {
    const name = window.prompt("Tên mẫu mới", currentName);
    if (!name?.trim() || name.trim() === currentName) return;
    const tpl = orgTemplates.find((t) => t.id === templateId);
    if (!tpl) return;
    try {
      await update.mutateAsync({
        templateId,
        body: {
          name: name.trim(),
          position: tpl.position,
          venueType: tpl.venueType,
          employmentType: tpl.employmentType,
          title: tpl.title,
          description: tpl.description,
          salaryMin:
            typeof tpl.salaryMin === "number"
              ? tpl.salaryMin
              : Number(tpl.salaryMin),
          salaryMax:
            typeof tpl.salaryMax === "number"
              ? tpl.salaryMax
              : Number(tpl.salaryMax),
          salaryType: tpl.salaryType,
          defaultHeadcount: tpl.defaultHeadcount,
          benefits: tpl.benefits,
          skillsRequired: tpl.skillsRequired,
          skillsPreferred: tpl.skillsPreferred,
          requirements: tpl.requirements,
          softNotes: tpl.softNotes,
        },
      });
      toast({ tone: "success", title: "Đã đổi tên mẫu" });
    } catch {
      toast({ tone: "error", title: "Đổi tên thất bại" });
    }
  }

  async function del(templateId: string, name: string) {
    if (!window.confirm(`Xoá mẫu "${name}"?`)) return;
    try {
      await remove.mutateAsync(templateId);
      toast({ tone: "success", title: "Đã xoá mẫu" });
    } catch {
      toast({ tone: "error", title: "Xoá thất bại" });
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-display-lg font-extrabold text-ink">
            Mẫu tin tuyển dụng
          </h1>
          <p className="mt-1 text-sm text-ink/65">
            Lưu lại nội dung tin đăng để lần sau chỉ cần chọn lại và đăng.
          </p>
        </div>
        <Link
          to="/postings/new"
          className="rounded-full bg-ink px-4 py-2 text-sm font-bold text-paper hover:bg-ink/90"
        >
          + Đăng tin mới
        </Link>
      </header>

      <section>
        <h2 className="mb-3 text-base font-bold text-ink">
          Mẫu của doanh nghiệp ({orgTemplates.length})
        </h2>
        {orgTemplates.length === 0 ? (
          <Card className="p-6 text-center text-sm text-ink/65">
            Bạn chưa lưu mẫu nào. Trong trang đăng tin, bấm{" "}
            <strong>Lưu thành mẫu</strong> để tạo mẫu đầu tiên.
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {orgTemplates.map((tpl) => (
              <Card key={tpl.id} className="space-y-2 p-4">
                <p className="font-bold text-ink">{tpl.name}</p>
                <p className="text-xs text-ink/60">{tpl.title}</p>
                <p className="line-clamp-2 text-xs text-ink/50">
                  {tpl.description}
                </p>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => rename(tpl.id, tpl.name)}
                  >
                    Đổi tên
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => del(tpl.id, tpl.name)}
                  >
                    Xoá
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-base font-bold text-ink">
          Mẫu hệ thống ({systemTemplates.length})
        </h2>
        <p className="mb-3 text-xs text-ink/55">
          Mẫu do hệ thống cung cấp — không sửa được, nhưng có thể chọn để fill
          form trong trang đăng tin.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {systemTemplates.map((tpl) => (
            <Card key={tpl.id} className="space-y-2 p-4 opacity-90">
              <p className="font-bold text-ink">{tpl.name}</p>
              <p className="text-xs text-ink/60">{tpl.title}</p>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
